import { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { publicApi } from '../services/api'
import { formatCurrency } from '../config'
import type { AmbassadorSubmission } from '../types'
import LoadingSpinner from '../components/common/LoadingSpinner'

const AFFILIATION_LABELS: Record<string, string> = {
  'Parent/Guardian': 'Parent/Guardian',
  'Educator': 'Educator',
  'Student 14-17': 'Student (14-17)',
  'Community Member': 'Community Member',
  'Business Owner': 'Business Owner',
}

const CONTACT_METHOD_LABELS: Record<string, string> = {
  email: 'Email',
  phone: 'Phone',
  both: 'Email & Phone',
}

export default function AmbassadorSuccess() {
  const [searchParams] = useSearchParams()
  const [submission, setSubmission] = useState<AmbassadorSubmission | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchSubmission() {
      const ambassadorId = searchParams.get('ambassadorId')
      if (!ambassadorId) {
        setError('No ambassador ID provided')
        setLoading(false)
        return
      }

      try {
        const response = await publicApi.getAmbassadorSubmission(ambassadorId)
        setSubmission(response.ambassador)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load submission')
      } finally {
        setLoading(false)
      }
    }
    fetchSubmission()
  }, [searchParams])

  if (loading) {
    return (
      <div className="py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !submission) {
    return (
      <div className="card text-center py-12">
        <p className="text-red-600 mb-4">{error || 'Submission not found'}</p>
        <Link to="/ambassador" className="btn btn-primary">
          Back to Ambassador Sign Up
        </Link>
      </div>
    )
  }

  const ambassadorName = `${submission.firstName} ${submission.lastName}`

  return (
    <div className="max-w-3xl mx-auto">
      {/* Success Header */}
      <div className="card text-center mb-8">
        <div className="mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Welcome to the Ambassador Team!
          </h1>
          <p className="text-xl text-gray-600">
            {ambassadorName}
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-gray-700">
            A confirmation email and receipt has been sent to <strong>{submission.email}</strong>
          </p>
        </div>
      </div>

      {/* Ambassador Summary */}
      <div className="card mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-6">Ambassador Registration Summary</h2>

        {/* Contact Information */}
        <div className="mb-6 pb-6 border-b">
          <h3 className="font-semibold text-gray-700 mb-3">Contact Information</h3>
          <div className="grid md:grid-cols-2 gap-2 text-gray-600">
            <p><strong>Name:</strong> {ambassadorName}</p>
            <p><strong>Email:</strong> {submission.email}</p>
            <p><strong>Phone:</strong> {submission.phone}</p>
            <p><strong>City:</strong> {submission.city}</p>
            <p><strong>Zip:</strong> {submission.zip}</p>
            <p><strong>Preferred Contact:</strong> {CONTACT_METHOD_LABELS[submission.preferredContactMethod] || submission.preferredContactMethod}</p>
          </div>
        </div>

        {/* Ambassador Details */}
        <div className="mb-6 pb-6 border-b">
          <h3 className="font-semibold text-gray-700 mb-3">Ambassador Details</h3>
          <div className="grid md:grid-cols-2 gap-2 text-gray-600">
            <p><strong>Affiliation:</strong> {AFFILIATION_LABELS[submission.affiliation] || submission.affiliation}</p>
            <p><strong>T-Shirt Size:</strong> {submission.tshirtSize}</p>
          </div>

          {submission.helpAreas && submission.helpAreas.length > 0 && (
            <div className="mt-4">
              <p className="font-medium text-gray-700 mb-2">Areas to Help:</p>
              <div className="flex flex-wrap gap-2">
                {submission.helpAreas.map((area) => (
                  <span key={area} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-sla-blue/10 text-sla-blue border border-sla-blue/20">
                    {area}
                  </span>
                ))}
              </div>
            </div>
          )}

          {submission.statementOfInterest && (
            <div className="mt-4">
              <p className="font-medium text-gray-700 mb-2">Statement of Interest:</p>
              <p className="text-gray-600 bg-gray-50 rounded-lg p-3 text-sm">{submission.statementOfInterest}</p>
            </div>
          )}

          {submission.fundraisingExpertise && (
            <div className="mt-4">
              <p className="font-medium text-gray-700 mb-2">Fundraising Expertise/Skills:</p>
              <p className="text-gray-600 bg-gray-50 rounded-lg p-3 text-sm">{submission.fundraisingExpertise}</p>
            </div>
          )}

          {submission.referralSource && (
            <div className="mt-4">
              <p className="text-gray-600">
                <strong>How you heard about us:</strong>{' '}
                {submission.referralSource === 'Other' && submission.referralSourceOther
                  ? `Other - ${submission.referralSourceOther}`
                  : submission.referralSource}
              </p>
            </div>
          )}
        </div>

        {/* Payment Summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold text-gray-700 mb-4">Payment Summary</h3>

          <div className="flex justify-between mb-2">
            <span className="text-gray-600">Ambassador Registration:</span>
            <span className="font-semibold text-green-600">{formatCurrency(submission.amountDueCents)}</span>
          </div>

          <div className="mt-4 pt-4 border-t text-sm text-gray-600">
            <p className="mb-1">
              <strong>Status:</strong>{' '}
              <span className={submission.status === 'paid' ? 'text-green-600' : 'text-yellow-600'}>
                {submission.status === 'paid' ? 'Paid' : 'Pending Payment'}
              </span>
            </p>
            <p><strong>Confirmation ID:</strong> {submission.ambassadorId}</p>
          </div>
        </div>
      </div>

      {/* What's Next */}
      <div className="card mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4">What's Next?</h2>
        <div className="space-y-4 text-gray-600">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 bg-sla-blue text-white rounded-full flex items-center justify-center text-sm font-semibold">1</div>
            <p>You'll receive your official Ambassador T-Shirt. We will contact you to arrange delivery.</p>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 bg-sla-blue text-white rounded-full flex items-center justify-center text-sm font-semibold">2</div>
            <p>We'll reach out to you via your preferred contact method about upcoming volunteer opportunities and events.</p>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 bg-sla-blue text-white rounded-full flex items-center justify-center text-sm font-semibold">3</div>
            <p>Keep an eye on our social media for updates about our mission and how you can help spread the word!</p>
          </div>
        </div>
      </div>

      {/* Tax Receipt Info */}
      <div className="card mb-8">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-bold text-gray-800">Tax Deductible Receipt</h2>
          <button
            onClick={() => window.print()}
            className="btn btn-secondary flex items-center gap-2 text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print
          </button>
        </div>
        <p className="text-gray-600 mb-4">
          School Lunch Angels is a 501(c)(3) non-profit organization. Your contribution may be tax-deductible.
        </p>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-gray-700"><strong>Organization:</strong> School Lunch Angels</p>
          <p className="text-gray-700"><strong>EIN:</strong> 33-5040677</p>
          <p className="text-gray-700"><strong>Paid Amount:</strong> {formatCurrency(submission.amountDueCents)}</p>
          <p className="text-gray-700"><strong>Date:</strong> {new Date(submission.createdAt).toLocaleDateString()}</p>
        </div>
        <p className="text-sm text-gray-500 mt-4">
          Please consult your tax advisor regarding the deductibility of your contribution.
        </p>
      </div>

      {/* Contact Info */}
      <div className="card text-center">
        <p className="text-gray-600 mb-4">
          Questions? Contact us at{' '}
          <a href="mailto:secretary@schoollunchangels.org" className="text-sla-blue hover:underline">
            secretary@schoollunchangels.org
          </a>
        </p>
        <p className="text-gray-500 text-sm">You may close this window.</p>
      </div>
    </div>
  )
}
